import Tooltip from "@mui/material/Tooltip";
import { labelActions } from '@common-reducers/labels-reducer'
import { _Label } from '@common-models/label'
import { GripVertical, Edit, Trash2, Users, Mail, AlertCircle, Clock, Star, Eye, EyeOff, Inbox, CheckCircle } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { useAppDispatch } from '@common-reducers/hooks/store.hook'
import { DBLabelThunks } from '@common-reducers/DBServiceThunks'
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';

type Props = {
    label: _Label
    attributes?: any
    listeners?: any
    setNodeRef?: (node: HTMLElement | null) => void
    style?: React.CSSProperties
    isDragging?: boolean
    onAction?: () => void
}

// Map label names to lucide-react icons or use emoji from label data
const getLabelIcon = (label: _Label) => {
    const iconClass = 'text-[var(--tasks-text-secondary)]';
    const iconSize = 18;

    // Otherwise, use lucide-react icons for default labels
    switch (label.name?.toLowerCase()) {
        case 'all':
            return <Users size={iconSize} className={iconClass} />;
        case 'inbox zero':
            return <Inbox size={iconSize} className={iconClass} />;
        case 'done':
            return <CheckCircle size={iconSize} className={iconClass} />;
        case 'unread':
            return <Mail size={iconSize} className={iconClass} />;
        case 'needs attention':
            return <AlertCircle size={iconSize} className={iconClass} />;
        case 'snoozed':
            return <Clock size={iconSize} className={iconClass} />;
        case 'starred':
            return <Star size={iconSize} className={iconClass} />;


    }
    // If label has an emoji icon or lucide icon name
    if (label.icon && label.icon.trim()) {
        // Check if it's a valid Lucide icon name
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const DynamicIcon = (LucideIcons as any)[label.icon];
        if (DynamicIcon) {
            return <DynamicIcon size={iconSize} className={iconClass} />;
        }
        // Fallback to emoji text
        return <span className="text-lg">{label.icon}</span>;
    }
    return null;

};

export default function LabelItem({ label, attributes, listeners, setNodeRef, style, isDragging, onAction }: Props) {
    const { t } = useTranslation(namespace)
    const dispatch = useAppDispatch()

    // Known system label names that should be translated
    const systemLabelNames = ['all', 'inbox', 'inbox zero', 'handle later', 'needs attention', 'unread', 'starred', 'done', 'snoozed'];

    // Get translated name for system labels
    const getTranslatedLabelName = (labelName: string, labelType?: string) => {
        // Check if it's a default type OR a known system label name
        if (labelType === 'default' || systemLabelNames.includes(labelName.toLowerCase())) {
            const key = `labels.systemLabels.${labelName.toLowerCase()}`;
            const translated = t(key);
            return translated !== key ? translated : labelName;
        }
        return labelName;
    };

    // Combine passed style with local styles
    const combinedStyle: React.CSSProperties = {
        ...style,
        boxSizing: 'border-box',
        zIndex: isDragging ? 100000 : 'auto', // Ensure dragged item is on top
        cursor: isDragging ? 'grabbing' : undefined,
        opacity: isDragging ? 0.5 : (label.isVisible === false ? 0.5 : 1), // Visual feedback for original item
        position: 'relative', // Needed for z-index to work
    };

    return (
        <div
            ref={setNodeRef}
            style={combinedStyle}
            className="flex items-center gap-2 p-2 rounded-lg hover:bg-[var(--tasks-menu-hover-bg)] transition-colors group"
        >
            <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing outline-none touch-none">
                <GripVertical size={18} className="text-[var(--tasks-text-muted)]" />
            </div>

            <button className="flex items-center gap-2 flex-1 text-left px-2 py-1 text-sm text-[var(--tasks-text)]">
                <span>{getLabelIcon(label)}</span>
                <span className="capitalize">{getTranslatedLabelName(label.name ?? '', label.type)}</span>
            </button>

            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Tooltip title={label.isVisible === false ? t('labels.showLabelInTabs') : t('labels.hideLabelFromTabs')} arrow slotProps={{ popper: { sx: { zIndex: 999999 } } }}>
                    <span style={{ display: 'inline-block' }}>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                dispatch(DBLabelThunks.patch({
                                    entity: {
                                        ...label,
                                        isVisible: label.isVisible === undefined ? false : !label.isVisible
                                    }
                                }));
                            }}
                            className="p-1.5 rounded-full transition-colors text-[var(--tasks-text-secondary)] hover:bg-[var(--tasks-menu-hover-bg)] cursor-pointer"
                        >
                            {label.isVisible === false ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                    </span>
                </Tooltip>

                <Tooltip title={label.type === 'default' ? t('labels.defaultLabelsCannotBeEdited') : t('labels.editLabel')} arrow slotProps={{ popper: { sx: { zIndex: 999999 } } }}>
                    <span style={{ display: 'inline-block' }}>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                if (label.type === 'custom') {
                                    dispatch(labelActions.toggleEditLabel(true))
                                    dispatch(labelActions.setLabel(label))
                                    onAction?.()
                                }
                            }}
                            disabled={label.type === 'default'}
                            className={`
                                p-1.5 rounded-full transition-colors
                                ${label.type === 'default'
                                    ? 'text-[var(--tasks-text-muted)] cursor-not-allowed opacity-30'
                                    : 'text-[var(--tasks-text-secondary)] hover:bg-[var(--tasks-menu-hover-bg)] cursor-pointer'
                                }
                            `}
                        >
                            <Edit size={16} />
                        </button>
                    </span>
                </Tooltip>

                <Tooltip title={label.type !== 'custom' ? t('labels.systemLabelsCannotBeDeleted') : t('labels.deleteLabel')} arrow slotProps={{ popper: { sx: { zIndex: 999999 } } }}>
                    <span style={{ display: 'inline-block' }}>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                if (label.type === 'custom') {
                                    dispatch(labelActions.toggleDeleteLabel(true))
                                    dispatch(labelActions.setLabel(label))
                                    onAction?.()
                                }
                            }}
                            disabled={label.type !== 'custom'}
                            className={`
                                p-1.5 rounded-full transition-colors
                                ${label.type !== 'custom'
                                    ? 'text-[var(--tasks-text-muted)] cursor-not-allowed opacity-30'
                                    : 'text-[var(--tasks-danger)] hover:opacity-80 rounded-full p-1.5 transition-colors cursor-pointer'
                                }
                            `}
                        >
                            <Trash2 size={16} />
                        </button>
                    </span>
                </Tooltip>
            </div>
        </div>
    )
}